import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Settings, Plus, Edit2, Trash2, Save, X, Check, 
  FileText, Clock, DollarSign, Users, Shield, 
  AlertTriangle, ChevronRight, RefreshCw, Copy,
  Calendar, Briefcase, CheckCircle, XCircle, Play
} from 'lucide-react';

// Rule categories
const RULE_CATEGORIES = [
  { value: 'leave', label: 'Leave Rules', icon: Calendar, color: 'blue' },
  { value: 'attendance', label: 'Attendance Rules', icon: Clock, color: 'emerald' },
  { value: 'payroll', label: 'Payroll Rules', icon: DollarSign, color: 'purple' },
  { value: 'approval', label: 'Approval Rules', icon: CheckCircle, color: 'amber' },
  { value: 'eligibility', label: 'Eligibility Rules', icon: Users, color: 'rose' },
];

// Rule operators
const RULE_OPERATORS = [
  { value: 'equals', label: 'Equals (=)' },
  { value: 'not_equals', label: 'Not Equals (≠)' },
  { value: 'greater_than', label: 'Greater Than (>)' },
  { value: 'less_than', label: 'Less Than (<)' },
  { value: 'greater_equal', label: 'Greater or Equal (≥)' },
  { value: 'less_equal', label: 'Less or Equal (≤)' },
  { value: 'in', label: 'In List' },
  { value: 'not_in', label: 'Not In List' },
  { value: 'contains', label: 'Contains' },
  { value: 'between', label: 'Between' },
  { value: 'is_null', label: 'Is Empty' },
  { value: 'is_not_null', label: 'Is Not Empty' },
];

// Rule actions
const RULE_ACTIONS = [
  { value: 'allow', label: 'Allow', color: 'emerald' },
  { value: 'deny', label: 'Deny', color: 'red' },
  { value: 'credit', label: 'Credit', color: 'blue' },
  { value: 'debit', label: 'Debit', color: 'orange' },
  { value: 'notify', label: 'Notify', color: 'purple' },
  { value: 'auto_approve', label: 'Auto Approve', color: 'green' },
  { value: 'require_approval', label: 'Require Approval', color: 'amber' },
  { value: 'calculate', label: 'Calculate', color: 'slate' },
];

const RuleConfiguration = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('rules');
  const [loading, setLoading] = useState(true);
  
  // Rules state
  const [rules, setRules] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Leave types state
  const [leaveTypes, setLeaveTypes] = useState([]);
  
  // Approval matrices state
  const [approvalMatrices, setApprovalMatrices] = useState([]);
  
  // Modal states
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showLeaveTypeModal, setShowLeaveTypeModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab, selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'rules') {
        const params = selectedCategory ? { category: selectedCategory } : {};
        const res = await api.get('/rule-engine/rules', { params });
        setRules(res.data.data || []);
      } else if (activeTab === 'leave-types') {
        const res = await api.get('/rule-engine/leave-types', { params: { active_only: false } });
        setLeaveTypes(res.data || []);
      } else if (activeTab === 'approval-matrix') {
        const res = await api.get('/rule-engine/approval-matrix');
        setApprovalMatrices(res.data || []);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm('Are you sure you want to deactivate this rule?')) return;
    try {
      await api.delete(`/rule-engine/rules/${ruleId}`);
      toast.success('Rule deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleDeleteLeaveType = async (code) => {
    if (!confirm('Are you sure you want to deactivate this leave type?')) return;
    try {
      await api.delete(`/rule-engine/leave-types/${code}`);
      toast.success('Leave type deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete leave type');
    }
  };

  const handleDeleteMatrix = async (matrixId) => {
    if (!confirm('Are you sure you want to deactivate this approval matrix?')) return;
    try {
      await api.delete(`/rule-engine/approval-matrix/${matrixId}`);
      toast.success('Approval matrix deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete matrix');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700',
      inactive: 'bg-slate-100 text-slate-500',
      draft: 'bg-amber-100 text-amber-700',
      expired: 'bg-red-100 text-red-700'
    };
    return styles[status] || styles.inactive;
  };

  const getCategoryIcon = (category) => {
    const cat = RULE_CATEGORIES.find(c => c.value === category);
    return cat?.icon || Settings;
  };

  return (
    <div data-testid="rule-configuration">
      <Header 
        title="Rule Configuration"
        subtitle="Configure leave policies, attendance rules, approval workflows, and more"
        action={
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-[500px]">
            <TabsTrigger value="rules" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Policy Rules
            </TabsTrigger>
            <TabsTrigger value="leave-types" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Leave Types
            </TabsTrigger>
            <TabsTrigger value="approval-matrix" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Approval Matrix
            </TabsTrigger>
          </TabsList>

          {/* Policy Rules Tab */}
          <TabsContent value="rules" className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {RULE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Badge variant="outline">{rules.length} rules</Badge>
              </div>
              <Button onClick={() => { setEditingItem(null); setShowRuleModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Rule
              </Button>
            </div>

            {/* Category Quick Filters */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {RULE_CATEGORIES.map(cat => {
                const Icon = cat.icon;
                const count = rules.filter(r => r.category === cat.value).length;
                const isActive = selectedCategory === cat.value;
                return (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(isActive ? '' : cat.value)}
                    className={`p-4 rounded-xl border transition-all ${
                      isActive 
                        ? `bg-${cat.color}-50 border-${cat.color}-300 ring-2 ring-${cat.color}-500` 
                        : 'bg-white hover:bg-slate-50 hover:border-slate-300'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mb-2 text-${cat.color}-600`} />
                    <p className="text-sm font-medium">{cat.label}</p>
                    <p className="text-2xl font-bold text-slate-700">{count}</p>
                  </button>
                );
              })}
            </div>

            {/* Rules List */}
            <div className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : rules.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Settings className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No rules configured yet</p>
                    <Button className="mt-4" onClick={() => setShowRuleModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Rule
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                rules.map(rule => {
                  const CategoryIcon = getCategoryIcon(rule.category);
                  return (
                    <Card key={rule.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="p-2 rounded-lg bg-slate-100">
                              <CategoryIcon className="w-5 h-5 text-slate-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-slate-900">{rule.name}</h3>
                                <Badge className={getStatusBadge(rule.status)}>
                                  {rule.status}
                                </Badge>
                                <Badge variant="outline">{rule.category}</Badge>
                              </div>
                              <p className="text-sm text-slate-500 mt-1">{rule.description || 'No description'}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                                <span>Priority: {rule.priority}</span>
                                <span>Action: {rule.action}</span>
                                <span>Conditions: {rule.conditions?.length || 0}</span>
                                <span>Effective: {rule.effective_from}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setEditingItem(rule); setShowRuleModal(true); }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteRule(rule.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Leave Types Tab */}
          <TabsContent value="leave-types" className="space-y-6">
            <div className="flex items-center justify-between">
              <Badge variant="outline">{leaveTypes.length} leave types</Badge>
              <Button onClick={() => { setEditingItem(null); setShowLeaveTypeModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Leave Type
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading ? (
                <div className="col-span-3 flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : leaveTypes.length === 0 ? (
                <Card className="col-span-3">
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No leave types configured</p>
                    <Button className="mt-4" onClick={() => setShowLeaveTypeModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Leave Type
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                leaveTypes.map(lt => (
                  <Card key={lt.id} className={`${!lt.is_active ? 'opacity-60' : ''}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <span className="font-bold text-blue-700">{lt.code}</span>
                          </div>
                          <div>
                            <CardTitle className="text-base">{lt.name}</CardTitle>
                            <Badge variant={lt.is_active ? 'default' : 'secondary'} className="text-xs">
                              {lt.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingItem(lt); setShowLeaveTypeModal(true); }}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteLeaveType(lt.code)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="p-2 bg-slate-50 rounded">
                          <p className="text-slate-500">Annual Credit</p>
                          <p className="font-semibold">{lt.annual_credit} days</p>
                        </div>
                        <div className="p-2 bg-slate-50 rounded">
                          <p className="text-slate-500">Max Accumulation</p>
                          <p className="font-semibold">{lt.max_accumulation || '∞'} days</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {lt.carry_forward_allowed && (
                          <Badge variant="outline" className="text-xs">Carry Forward</Badge>
                        )}
                        {lt.encashment_allowed && (
                          <Badge variant="outline" className="text-xs">Encashable</Badge>
                        )}
                        {lt.can_be_half_day && (
                          <Badge variant="outline" className="text-xs">Half Day</Badge>
                        )}
                        {lt.requires_document && (
                          <Badge variant="outline" className="text-xs">Doc Required</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Approval Matrix Tab */}
          <TabsContent value="approval-matrix" className="space-y-6">
            <div className="flex items-center justify-between">
              <Badge variant="outline">{approvalMatrices.length} matrices</Badge>
              <Button onClick={() => { setEditingItem(null); setShowApprovalModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Approval Matrix
              </Button>
            </div>

            <div className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : approvalMatrices.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No approval matrices configured</p>
                    <Button className="mt-4" onClick={() => setShowApprovalModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Approval Matrix
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                approvalMatrices.map(matrix => (
                  <Card key={matrix.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{matrix.name}</h3>
                            <Badge variant="outline">{matrix.request_type}</Badge>
                            <Badge>{matrix.is_active ? 'Active' : 'Inactive'}</Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            {matrix.levels?.length || 0} approval levels • Priority: {matrix.priority}
                          </p>
                          <div className="flex items-center gap-2 mt-3">
                            {matrix.levels?.map((level, idx) => (
                              <React.Fragment key={idx}>
                                <div className="px-3 py-1.5 bg-slate-100 rounded-lg text-sm">
                                  <span className="font-medium">L{level.level}:</span> {level.approver_type}
                                </div>
                                {idx < matrix.levels.length - 1 && (
                                  <ChevronRight className="w-4 h-4 text-slate-400" />
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingItem(matrix); setShowApprovalModal(true); }}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteMatrix(matrix.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Rule Modal */}
      {showRuleModal && (
        <RuleModal
          rule={editingItem}
          onClose={() => { setShowRuleModal(false); setEditingItem(null); }}
          onSave={() => { setShowRuleModal(false); setEditingItem(null); fetchData(); }}
        />
      )}

      {/* Leave Type Modal */}
      {showLeaveTypeModal && (
        <LeaveTypeModal
          leaveType={editingItem}
          onClose={() => { setShowLeaveTypeModal(false); setEditingItem(null); }}
          onSave={() => { setShowLeaveTypeModal(false); setEditingItem(null); fetchData(); }}
        />
      )}

      {/* Approval Matrix Modal */}
      {showApprovalModal && (
        <ApprovalMatrixModal
          matrix={editingItem}
          onClose={() => { setShowApprovalModal(false); setEditingItem(null); }}
          onSave={() => { setShowApprovalModal(false); setEditingItem(null); fetchData(); }}
        />
      )}
    </div>
  );
};

// Rule Modal Component
const RuleModal = ({ rule, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    description: rule?.description || '',
    category: rule?.category || 'leave',
    priority: rule?.priority || 0,
    action: rule?.action || 'allow',
    effective_from: rule?.effective_from || new Date().toISOString().split('T')[0],
    effective_to: rule?.effective_to || '',
    conditions: rule?.conditions || [],
    action_params: rule?.action_params || {},
    applies_to: rule?.applies_to || {}
  });
  const [saving, setSaving] = useState(false);

  const handleAddCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: '', operator: 'equals', value: '', logical_operator: 'AND' }]
    }));
  };

  const handleRemoveCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const handleConditionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category || !formData.effective_from) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      if (rule?.id) {
        await api.put(`/rule-engine/rules/${rule.id}`, formData);
        toast.success('Rule updated');
      } else {
        await api.post('/rule-engine/rules', formData);
        toast.success('Rule created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{rule ? 'Edit Rule' : 'Create New Rule'}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)] space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Rule Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Max Casual Leave Per Month"
              />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this rule does..."
                rows={2}
              />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RULE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Action *</Label>
              <Select value={formData.action} onValueChange={(v) => setFormData({ ...formData, action: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RULE_ACTIONS.map(action => (
                    <SelectItem key={action.value} value={action.value}>{action.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Effective From *</Label>
              <Input
                type="date"
                value={formData.effective_from}
                onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
              />
            </div>
            <div>
              <Label>Effective To (Optional)</Label>
              <Input
                type="date"
                value={formData.effective_to}
                onChange={(e) => setFormData({ ...formData, effective_to: e.target.value })}
              />
            </div>
          </div>

          {/* Conditions */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Conditions</Label>
              <Button size="sm" variant="outline" onClick={handleAddCondition}>
                <Plus className="w-3 h-3 mr-1" /> Add Condition
              </Button>
            </div>
            {formData.conditions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No conditions. Rule will match all requests.</p>
            ) : (
              formData.conditions.map((condition, idx) => (
                <div key={condition.id || `condition-${idx}`} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  {idx > 0 && (
                    <Select
                      value={condition.logical_operator}
                      onValueChange={(v) => handleConditionChange(idx, 'logical_operator', v)}
                    >
                      <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">AND</SelectItem>
                        <SelectItem value="OR">OR</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Input
                    placeholder="Field (e.g., employee.grade)"
                    value={condition.field}
                    onChange={(e) => handleConditionChange(idx, 'field', e.target.value)}
                    className="flex-1"
                  />
                  <Select
                    value={condition.operator}
                    onValueChange={(v) => handleConditionChange(idx, 'operator', v)}
                  >
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RULE_OPERATORS.map(op => (
                        <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Value"
                    value={condition.value}
                    onChange={(e) => handleConditionChange(idx, 'value', e.target.value)}
                    className="w-32"
                  />
                  <Button size="sm" variant="ghost" onClick={() => handleRemoveCondition(idx)}>
                    <X className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Rule</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Leave Type Modal Component
const LeaveTypeModal = ({ leaveType, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    code: leaveType?.code || '',
    name: leaveType?.name || '',
    description: leaveType?.description || '',
    annual_credit: leaveType?.annual_credit || 0,
    credit_frequency: leaveType?.credit_frequency || 'yearly',
    carry_forward_allowed: leaveType?.carry_forward_allowed || false,
    max_carry_forward: leaveType?.max_carry_forward || 0,
    encashment_allowed: leaveType?.encashment_allowed || false,
    max_encashment_per_year: leaveType?.max_encashment_per_year || 0,
    max_accumulation: leaveType?.max_accumulation || 0,
    min_days_per_application: leaveType?.min_days_per_application || 0.5,
    max_days_per_application: leaveType?.max_days_per_application || 0,
    advance_notice_days: leaveType?.advance_notice_days || 0,
    can_be_half_day: leaveType?.can_be_half_day ?? true,
    requires_document: leaveType?.requires_document || false,
    auto_approve_up_to_days: leaveType?.auto_approve_up_to_days || 0,
    approval_levels: leaveType?.approval_levels || 1,
    include_weekends: leaveType?.include_weekends || false,
    include_holidays: leaveType?.include_holidays || false,
    allow_negative_balance: leaveType?.allow_negative_balance || false
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      toast.error('Please fill in code and name');
      return;
    }
    setSaving(true);
    try {
      if (leaveType?.id) {
        await api.put(`/rule-engine/leave-types/${leaveType.code}`, formData);
        toast.success('Leave type updated');
      } else {
        await api.post('/rule-engine/leave-types', formData);
        toast.success('Leave type created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save leave type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{leaveType ? 'Edit Leave Type' : 'Create Leave Type'}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)] space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., CL, EL, SL"
                maxLength={5}
                disabled={!!leaveType}
              />
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Casual Leave"
              />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          {/* Credit Rules */}
          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="font-medium">Credit Rules</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Annual Credit (days)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.annual_credit}
                  onChange={(e) => setFormData({ ...formData, annual_credit: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Credit Frequency</Label>
                <Select value={formData.credit_frequency} onValueChange={(v) => setFormData({ ...formData, credit_frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yearly">Yearly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max Accumulation (0 = unlimited)</Label>
                <Input
                  type="number"
                  value={formData.max_accumulation}
                  onChange={(e) => setFormData({ ...formData, max_accumulation: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>

          {/* Carry Forward & Encashment */}
          <div className="grid grid-cols-2 gap-4">
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Carry Forward</h3>
                <Switch
                  checked={formData.carry_forward_allowed}
                  onCheckedChange={(v) => setFormData({ ...formData, carry_forward_allowed: v })}
                />
              </div>
              {formData.carry_forward_allowed && (
                <div>
                  <Label>Max Carry Forward (days)</Label>
                  <Input
                    type="number"
                    value={formData.max_carry_forward}
                    onChange={(e) => setFormData({ ...formData, max_carry_forward: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              )}
            </div>
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Encashment</h3>
                <Switch
                  checked={formData.encashment_allowed}
                  onCheckedChange={(v) => setFormData({ ...formData, encashment_allowed: v })}
                />
              </div>
              {formData.encashment_allowed && (
                <div>
                  <Label>Max Encashment/Year (days)</Label>
                  <Input
                    type="number"
                    value={formData.max_encashment_per_year}
                    onChange={(e) => setFormData({ ...formData, max_encashment_per_year: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Application Rules */}
          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="font-medium">Application Rules</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Min Days/Application</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.min_days_per_application}
                  onChange={(e) => setFormData({ ...formData, min_days_per_application: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Max Days/Application (0 = no limit)</Label>
                <Input
                  type="number"
                  value={formData.max_days_per_application}
                  onChange={(e) => setFormData({ ...formData, max_days_per_application: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Advance Notice (days)</Label>
                <Input
                  type="number"
                  value={formData.advance_notice_days}
                  onChange={(e) => setFormData({ ...formData, advance_notice_days: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.can_be_half_day}
                  onCheckedChange={(v) => setFormData({ ...formData, can_be_half_day: v })}
                />
                <Label>Allow Half Day</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.requires_document}
                  onCheckedChange={(v) => setFormData({ ...formData, requires_document: v })}
                />
                <Label>Requires Document</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.include_weekends}
                  onCheckedChange={(v) => setFormData({ ...formData, include_weekends: v })}
                />
                <Label>Include Weekends</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.allow_negative_balance}
                  onCheckedChange={(v) => setFormData({ ...formData, allow_negative_balance: v })}
                />
                <Label>Allow Negative Balance</Label>
              </div>
            </div>
          </div>

          {/* Approval */}
          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="font-medium">Approval Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Auto Approve Up To (days)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.auto_approve_up_to_days}
                  onChange={(e) => setFormData({ ...formData, auto_approve_up_to_days: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Approval Levels Required</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.approval_levels}
                  onChange={(e) => setFormData({ ...formData, approval_levels: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Leave Type</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Approval Matrix Modal Component
const ApprovalMatrixModal = ({ matrix, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: matrix?.name || '',
    request_type: matrix?.request_type || 'leave',
    applies_to_leave_types: matrix?.applies_to_leave_types || [],
    applies_to_grades: matrix?.applies_to_grades || [],
    applies_to_departments: matrix?.applies_to_departments || [],
    min_days: matrix?.min_days || 0,
    max_days: matrix?.max_days || 0,
    levels: matrix?.levels || [{ level: 1, approver_type: 'reporting_manager', can_skip: false, auto_approve_after_days: 0 }],
    parallel_approval: matrix?.parallel_approval || false,
    any_one_approval: matrix?.any_one_approval || false,
    priority: matrix?.priority || 0
  });
  const [saving, setSaving] = useState(false);

  const handleAddLevel = () => {
    const nextLevel = formData.levels.length + 1;
    setFormData(prev => ({
      ...prev,
      levels: [...prev.levels, { level: nextLevel, approver_type: 'department_head', can_skip: false, auto_approve_after_days: 0 }]
    }));
  };

  const handleRemoveLevel = (index) => {
    setFormData(prev => ({
      ...prev,
      levels: prev.levels.filter((_, i) => i !== index).map((l, i) => ({ ...l, level: i + 1 }))
    }));
  };

  const handleLevelChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      levels: prev.levels.map((l, i) => i === index ? { ...l, [field]: value } : l)
    }));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.request_type) {
      toast.error('Please fill in name and request type');
      return;
    }
    setSaving(true);
    try {
      if (matrix?.id) {
        await api.put(`/rule-engine/approval-matrix/${matrix.id}`, formData);
        toast.success('Approval matrix updated');
      } else {
        await api.post('/rule-engine/approval-matrix', formData);
        toast.success('Approval matrix created');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{matrix ? 'Edit Approval Matrix' : 'Create Approval Matrix'}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)] space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Matrix Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Standard Leave Approval"
              />
            </div>
            <div>
              <Label>Request Type *</Label>
              <Select value={formData.request_type} onValueChange={(v) => setFormData({ ...formData, request_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="leave">Leave Request</SelectItem>
                  <SelectItem value="attendance">Attendance Regularization</SelectItem>
                  <SelectItem value="claim">Expense Claim</SelectItem>
                  <SelectItem value="overtime">Overtime Request</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Min Days (0 = any)</Label>
              <Input
                type="number"
                step="0.5"
                value={formData.min_days}
                onChange={(e) => setFormData({ ...formData, min_days: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Max Days (0 = unlimited)</Label>
              <Input
                type="number"
                step="0.5"
                value={formData.max_days}
                onChange={(e) => setFormData({ ...formData, max_days: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          {/* Approval Levels */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Approval Levels</h3>
              <Button size="sm" variant="outline" onClick={handleAddLevel}>
                <Plus className="w-3 h-3 mr-1" /> Add Level
              </Button>
            </div>
            {formData.levels.map((level, idx) => (
              <div key={level.id || `level-${level.level}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                  {level.level}
                </div>
                <Select
                  value={level.approver_type}
                  onValueChange={(v) => handleLevelChange(idx, 'approver_type', v)}
                >
                  <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reporting_manager">Reporting Manager</SelectItem>
                    <SelectItem value="department_head">Department Head</SelectItem>
                    <SelectItem value="hr">HR Department</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="ceo">CEO</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={level.can_skip}
                    onCheckedChange={(v) => handleLevelChange(idx, 'can_skip', v)}
                  />
                  <span className="text-xs">Can Skip</span>
                </div>
                {formData.levels.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => handleRemoveLevel(idx)}>
                    <X className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Options */}
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.parallel_approval}
                onCheckedChange={(v) => setFormData({ ...formData, parallel_approval: v })}
              />
              <Label>Parallel Approval</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.any_one_approval}
                onCheckedChange={(v) => setFormData({ ...formData, any_one_approval: v })}
              />
              <Label>Any One Can Approve</Label>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Matrix</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RuleConfiguration;
